import React from 'react';
import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Link,
  Divider,
  UnorderedList,
  ListItem
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import Footer from './Footer';

const PrivacyPolicy = () => (
  <Container maxW="container.md" py={8}>
    <VStack spacing={6} align="stretch">
      <Heading as="h1" size="xl">Privacy Policy</Heading>
      <Text fontSize="sm" color="gray.600">Last Updated: April 3, 2026</Text>
      <Text fontSize="sm" color="gray.600">
        Operated by <strong>Setora, LLC</strong> on behalf of PlayMaker Pics clients
      </Text>
      <Divider />

      <Box>
        <Heading as="h2" size="lg" mb={4}>1. Introduction</Heading>
        <Text mb={3}>
          Your privacy is important to us. This Privacy Policy explains how PlayMaker Pics, a
          service operated by <strong>Setora, LLC</strong>, collects, uses, stores, and protects
          your personal information, including information collected in connection with our SMS
          messaging program.
        </Text>
        <Text>
          By using our services or opting in to receive SMS messages, you consent to the collection
          and use of information in accordance with this policy.
        </Text>
      </Box>

      <Box>
        <Heading as="h2" size="lg" mb={4}>2. Information We Collect</Heading>
        <Text mb={3}>We may collect the following information from you:</Text>
        <UnorderedList mb={3} pl={5}>
          <ListItem>Personal information such as name, email address, and phone number</ListItem>
          <ListItem>Mobile phone number provided for SMS opt-in</ListItem>
          <ListItem>SMS opt-in consent status and date of consent</ListItem>
          <ListItem>Demographic information such as location and team affiliation</ListItem>
          <ListItem>Athlete information including name, jersey number, and team details</ListItem>
          <ListItem>Images and photographs uploaded to or taken through our service</ListItem>
          <ListItem>Information about your usage of our service, including order history</ListItem>
          <ListItem>Device information and IP address for security purposes</ListItem>
        </UnorderedList>
      </Box>

      <Box>
        <Heading as="h2" size="lg" mb={4}>3. How We Use Your Information</Heading>
        <Text mb={3}>We use the information we collect to:</Text>
        <UnorderedList mb={3} pl={5}>
          <ListItem>Provide, maintain, and improve our photography and graphics services</ListItem>
          <ListItem>Send SMS notifications about new photos, order updates, event reminders, and account activity (only with your explicit consent)</ListItem>
          <ListItem>Process payments and fulfill orders</ListItem>
          <ListItem>Communicate with you about your account and our services</ListItem>
          <ListItem>Organize and share photos from events you participate in</ListItem>
          <ListItem>Verify your identity and prevent fraud</ListItem>
          <ListItem>Comply with legal obligations</ListItem>
        </UnorderedList>
      </Box>

      <Box>
        <Heading as="h2" size="lg" mb={4}>4. SMS-Specific Data Practices</Heading>
        <Text mb={3}>
          When you opt in to our SMS program, we collect your mobile phone number and record your
          consent. Regarding your SMS data:
        </Text>
        <UnorderedList mb={3} pl={5}>
          <ListItem>
            <strong>Your phone number and SMS consent information will not be shared with third
            parties for their marketing purposes.</strong>
          </ListItem>
          <ListItem>
            We will not sell, rent, loan, or trade your phone number or opt-in data to any
            third party.
          </ListItem>
          <ListItem>
            Your phone number is used solely for sending you the SMS notifications you consented to.
          </ListItem>
          <ListItem>
            We may share your phone number with our SMS service provider (Twilio) solely for the
            purpose of delivering messages on our behalf. Our service provider is contractually
            bound to keep your information confidential and to use it only for delivering our messages.
          </ListItem>
          <ListItem>
            We retain your phone number and consent records for as long as you are opted in, plus
            a reasonable period after opt-out for compliance record-keeping.
          </ListItem>
        </UnorderedList>
      </Box>

      <Box>
        <Heading as="h2" size="lg" mb={4}>5. Information Sharing and Disclosure</Heading>
        <Text mb={3}>
          We do <strong>not</strong> sell, rent, or share your personal information with third
          parties for marketing purposes. We may share your information only in the following
          limited circumstances:
        </Text>
        <UnorderedList mb={3} pl={5}>
          <ListItem>
            <strong>Service Providers:</strong> With trusted third-party vendors who help us
            operate our service (e.g., payment processors, cloud hosting, SMS delivery), subject
            to confidentiality agreements.
          </ListItem>
          <ListItem>
            <strong>Sports Organizations:</strong> With the youth sports organization that you
            are affiliated with, limited to information necessary for coordinating photo services.
          </ListItem>
          <ListItem>
            <strong>Legal Compliance:</strong> When required by law, regulation, legal process,
            or governmental request.
          </ListItem>
          <ListItem>
            <strong>Safety:</strong> To protect the rights, property, or safety of Setora, LLC,
            PlayMaker Pics, our users, or the public.
          </ListItem>
        </UnorderedList>
      </Box>

      <Box>
        <Heading as="h2" size="lg" mb={4}>6. Children's Privacy (COPPA Compliance)</Heading>
        <Text mb={3}>
          We take children's privacy seriously and comply with the Children's Online Privacy
          Protection Act (COPPA).
        </Text>
        <Heading as="h3" size="md" mb={2}>6.1 Information Collection from Children</Heading>
        <Text mb={3}>
          We only collect personal information from children under 13 with verifiable parental
          consent. The information we may collect includes:
        </Text>
        <UnorderedList mb={3} pl={5}>
          <ListItem>Child's first and last name</ListItem>
          <ListItem>Team affiliation and jersey number</ListItem>
          <ListItem>Photos and videos from sporting events</ListItem>
        </UnorderedList>
        <Heading as="h3" size="md" mb={2}>6.2 Parental Consent</Heading>
        <Text mb={3}>
          Before collecting personal information from a child under 13, we require a parent or
          guardian to provide consent through our registration process and confirm they are the
          parent or legal guardian of the child.
        </Text>
        <Heading as="h3" size="md" mb={2}>6.3 Parental Rights and Controls</Heading>
        <Text mb={3}>Parents or guardians of children under 13 have the right to:</Text>
        <UnorderedList mb={3} pl={5}>
          <ListItem>Review their child's personal information</ListItem>
          <ListItem>Request deletion of their child's information</ListItem>
          <ListItem>Refuse further collection or use of their child's information</ListItem>
          <ListItem>Revoke previously provided consent</ListItem>
        </UnorderedList>
        <Text mb={3}>
          To exercise these rights, please contact us at{' '}
          <Link href="mailto:support@playmakerpics.com" color="blue.500">support@playmakerpics.com</Link>.
        </Text>
        <Heading as="h3" size="md" mb={2}>6.4 Use and Disclosure of Children's Information</Heading>
        <Text mb={3}>
          We use children's personal information solely to provide our services. We do not:
        </Text>
        <UnorderedList mb={3} pl={5}>
          <ListItem>Use children's personal information for marketing purposes</ListItem>
          <ListItem>Share children's personal information with third parties for their own purposes</ListItem>
          <ListItem>Condition a child's participation on providing more information than is reasonably necessary</ListItem>
          <ListItem>Send SMS messages directly to children</ListItem>
        </UnorderedList>
      </Box>

      <Box>
        <Heading as="h2" size="lg" mb={4}>7. Data Security</Heading>
        <Text mb={3}>
          We implement appropriate technical and organizational security measures to protect your
          personal information from unauthorized access, alteration, disclosure, or destruction,
          including:
        </Text>
        <UnorderedList mb={3} pl={5}>
          <ListItem>Encryption of data in transit and at rest</ListItem>
          <ListItem>Secure authentication and access controls</ListItem>
          <ListItem>Regular security reviews and updates</ListItem>
          <ListItem>Limited employee access to personal information</ListItem>
        </UnorderedList>
        <Text>
          While we strive to use commercially acceptable means to protect your personal information,
          no method of electronic storage or transmission is 100% secure.
        </Text>
      </Box>

      <Box>
        <Heading as="h2" size="lg" mb={4}>8. Data Retention</Heading>
        <Text mb={3}>
          We retain your personal information for as long as your account is active or as needed to
          provide you services. We may retain certain information after account closure for compliance,
          legal, or legitimate business purposes. SMS consent records are retained for a minimum of
          5 years after opt-out for regulatory compliance.
        </Text>
      </Box>

      <Box>
        <Heading as="h2" size="lg" mb={4}>9. Your Rights</Heading>
        <Text mb={3}>You have the right to:</Text>
        <UnorderedList mb={3} pl={5}>
          <ListItem>Access and review your personal information</ListItem>
          <ListItem>Request correction of inaccurate information</ListItem>
          <ListItem>Request deletion of your personal information</ListItem>
          <ListItem>Opt out of SMS communications at any time by replying <strong>STOP</strong></ListItem>
          <ListItem>Withdraw consent for data processing</ListItem>
        </UnorderedList>
        <Text>
          To exercise any of these rights, contact us at{' '}
          <Link href="mailto:support@playmakerpics.com" color="blue.500">support@playmakerpics.com</Link>.
        </Text>
      </Box>

      <Box>
        <Heading as="h2" size="lg" mb={4}>10. California Privacy Rights (CCPA)</Heading>
        <Text mb={3}>
          If you are a California resident, you have additional rights under the California Consumer
          Privacy Act (CCPA), including the right to know what personal information we collect, the
          right to request deletion, and the right to opt out of the sale of personal information.
          We do not sell your personal information.
        </Text>
      </Box>

      <Box>
        <Heading as="h2" size="lg" mb={4}>11. Changes to This Policy</Heading>
        <Text mb={3}>
          We may update this Privacy Policy from time to time. We will notify you of any material
          changes by posting the new Privacy Policy on this page and updating the "Last Updated"
          date. For significant changes, we may also notify you via email or SMS.
        </Text>
      </Box>

      <Divider my={4} />

      <Box>
        <Heading as="h2" size="lg" mb={4}>Contact Us</Heading>
        <Text mb={2}><strong>Setora, LLC</strong> (operating as PlayMaker Pics)</Text>
        <Text mb={2}>
          Email:{' '}
          <Link href="mailto:support@playmakerpics.com" color="blue.500">support@playmakerpics.com</Link>
        </Text>
        <Text mb={4}>
          Website:{' '}
          <Link href="https://www.playmakerpics.com" color="blue.500" isExternal>www.playmakerpics.com</Link>
        </Text>
        <VStack spacing={1} align="start">
          <Link as={RouterLink} to="/terms" color="blue.500">Terms of Service</Link>
          <Link as={RouterLink} to="/opt-in-policy" color="blue.500">SMS Opt-In Policy</Link>
        </VStack>
      </Box>
    </VStack>
    <Footer />
  </Container>
);

export default PrivacyPolicy;
